import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { useSnackbar } from "notistack";

import TableComponent from "../../components/table";
import CardComponent from "../../components/Card";
import ButtonWithLoading from "../../components/ButtonWithLoading";
import DialogComponent from "../../components/Dialog";
import DialogActions from "../../components/DialogActions";
import { Get } from "../../actions";
import {
  Get_AllCronJobsWithPagination_URL,
  Get_CronJobFilterOptions_URL,
  Get_CronSchedules_URL,
  Get_DeleteCronJob_URL,
  Get_RunCronJobNow_URL,
  Get_SetCronJobActive_URL,
} from "../../constants/apiUrls";
import { getTranslation } from "../../heplers/translationHelper";
import { format } from "../../heplers/format";
import AddEditCronJob from "./AddEditCronJob";
import CronJobDetailsDialog from "./CronJobDetailsDialog";

const useStyles = makeStyles(() => ({
  route: {
    fontFamily: "monospace",
  },
}));

const statusColor = (statusName) => {
  switch (statusName) {
    case "Failed":
      return { backgroundColor: "#fdecea", color: "#b71c1c" };
    case "Succeeded":
      return { backgroundColor: "#e6f4ea", color: "#1b5e20" };
    case "Skipped":
      return { backgroundColor: "#fff4e5", color: "#8a5300" };
    default:
      return { backgroundColor: "#eeeeee", color: "#424242" };
  }
};

/**
 * When each cron run actually fires. A job list that looks healthy while the scheduler is off is
 * exactly the situation this panel exists to make visible.
 */
const SchedulePanel = ({ schedules }) => {
  if (!schedules) return null;

  return (
    <Box mb={2}>
      <Grid container spacing={2}>
        {schedules.schedules?.map((item) => (
          <Grid item xs={12} md={3} key={item.execution}>
            <Typography variant="body2">
              <b>{item.executionName}</b>
            </Typography>
            <Typography variant="caption" display="block" color="textSecondary">
              {item.expression}
            </Typography>
            <Typography variant="caption" display="block">
              {getTranslation("Next", "Prochain", "Nächster")}:{" "}
              {item.nextRun ? format("dateTime", item.nextRun) : "-"}
            </Typography>
          </Grid>
        ))}
        <Grid item xs={12} md={3}>
          <Chip
            size="small"
            label={
              schedules.enabled && schedules.scheduled
                ? `${getTranslation(
                    "Scheduler running",
                    "Planificateur actif",
                    "Scheduler läuft"
                  )} (${schedules.timeZone})`
                : getTranslation(
                    "Scheduler disabled - jobs only run when triggered",
                    "Planificateur désactivé - exécution manuelle uniquement",
                    "Scheduler deaktiviert - nur manuelle Ausführung"
                  )
            }
            style={
              schedules.enabled && schedules.scheduled
                ? { backgroundColor: "#e6f4ea", color: "#1b5e20" }
                : { backgroundColor: "#fff4e5", color: "#8a5300" }
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const Cron = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [cronJobs, setCronJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [levels, setLevels] = useState([]);
  const [schedules, setSchedules] = useState(null);

  const [appId, setAppId] = useState("");
  const [execution, setExecution] = useState("");
  const [status, setStatus] = useState("");
  const [searchString, setSearchString] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [editJob, setEditJob] = useState(null);
  const [logsJob, setLogsJob] = useState(null);
  const [deleteJob, setDeleteJob] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const [page, setPage] = useState(0);
  let pageSize = localStorage.getItem("sizeCronJobs");
  if (pageSize == null) {
    localStorage.setItem("sizeCronJobs", 10);
    pageSize = 10;
  }
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);

  useEffect(() => {
    Get(
      {},
      Get_CronJobFilterOptions_URL,
      null,
      (resp) => {
        setApps(resp?.data?.apps ?? []);
        setExecutions(resp?.data?.executions ?? []);
        setLevels(resp?.data?.levels ?? []);
      },
      () => {}
    );

    Get(
      {},
      Get_CronSchedules_URL,
      null,
      (resp) => setSchedules(resp?.data ?? null),
      () => {}
    );
  }, []);

  useEffect(() => {
    getCronJobs();
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const getCronJobs = () => {
    setIsLoading(true);
    const params = {
      searchString: searchString,
      pageNumber: page,
      pageSize: rowsPerPage,
      sort: "appName",
      sortDirection: 0,
    };
    // Leave the filter out entirely when "All" is selected.
    if (appId !== "") params.appId = appId;
    if (execution !== "") params.execution = execution;
    if (status !== "") params.isActive = status === "active";

    Get(
      params,
      Get_AllCronJobsWithPagination_URL,
      null,
      (resp) => {
        setCronJobs(resp.data);
        setIsLoading(false);
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setIsLoading(false);
      }
    );
  };

  const applyFilter = () => {
    if (page !== 0) setPage(0);
    else getCronJobs();
  };

  const handleChangeRowsPerPage = (event) => {
    localStorage.setItem("sizeCronJobs", parseInt(event.target.value, 10));
    setRowsPerPage(localStorage.getItem("sizeCronJobs"));
    setPage(0);
  };

  const runNow = (row) => {
    setRunningId(row.id);
    Get(
      { id: row.id },
      Get_RunCronJobNow_URL,
      null,
      (resp) => {
        const summary = resp?.data;
        enqueueSnackbar(
          `${row.route}: ${getTranslation(
            "triggered",
            "déclenché",
            "ausgelöst"
          )} ${summary?.triggered ?? 0}, ${getTranslation(
            "succeeded",
            "réussi",
            "erfolgreich"
          )} ${summary?.succeeded ?? 0}, ${getTranslation(
            "failed",
            "échoué",
            "fehlgeschlagen"
          )} ${summary?.failed ?? 0}`,
          { variant: summary?.failed > 0 ? "warning" : "success" }
        );
        setRunningId(null);
        getCronJobs();
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setRunningId(null);
      }
    );
  };

  const toggleActive = (row) => {
    Get(
      { id: row.id, isActive: !row.isActive },
      Get_SetCronJobActive_URL,
      null,
      () => {
        enqueueSnackbar(
          row.isActive
            ? getTranslation("Cron job disabled", "Tâche cron désactivée", "Cron-Job deaktiviert")
            : getTranslation("Cron job enabled", "Tâche cron activée", "Cron-Job aktiviert"),
          { variant: "success" }
        );
        getCronJobs();
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
      }
    );
  };

  const confirmDelete = () => {
    Get(
      { id: deleteJob.id },
      Get_DeleteCronJob_URL,
      null,
      () => {
        enqueueSnackbar(
          getTranslation("Cron job deleted", "Tâche cron supprimée", "Cron-Job gelöscht"),
          { variant: "success" }
        );
        setDeleteJob(null);
        getCronJobs();
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setDeleteJob(null);
      }
    );
  };

  const columns = [
    {
      id: "appName",
      numeric: false,
      disablePadding: true,
      label: getTranslation("App", "Application", "Anwendung"),
      component: ({ row }) => <Typography noWrap>{row?.appName}</Typography>,
    },
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Name", "Nom", "Name"),
      component: ({ row }) => (
        <Tooltip title={row?.description || ""}>
          <Typography noWrap>{row?.name}</Typography>
        </Tooltip>
      ),
    },
    {
      id: "route",
      numeric: false,
      disablePadding: false,
      label: getTranslation("Route", "Route", "Route"),
      component: ({ row }) => (
        <Tooltip title={row?.effectiveBaseUrl || ""}>
          <Typography className={classes.route}>{row?.route}</Typography>
        </Tooltip>
      ),
    },
    {
      id: "executionName",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Type", "Type", "Typ"),
      component: ({ row }) => <Typography noWrap>{row?.executionName}</Typography>,
    },
    {
      id: "isActive",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Status", "Statut", "Status"),
      component: ({ row }) => (
        <Chip
          size="small"
          label={
            row?.isActive
              ? getTranslation("Active", "Actif", "Aktiv")
              : getTranslation("Disabled", "Désactivé", "Deaktiviert")
          }
          style={
            row?.isActive
              ? { backgroundColor: "#e6f4ea", color: "#1b5e20" }
              : { backgroundColor: "#eeeeee", color: "#424242" }
          }
        />
      ),
    },
    {
      id: "lastRunAt",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Last run", "Dernière exécution", "Letzter Lauf"),
      component: ({ row }) => (
        <Tooltip title={row?.lastRunMessage || ""}>
          <Box display="flex" alignItems="center">
            <Typography noWrap>
              {row?.lastRunAt ? format("dateTime", row.lastRunAt) : "-"}
            </Typography>
            {row?.lastRunStatusName ? (
              <Box ml={1}>
                <Chip
                  size="small"
                  label={row.lastRunStatusName}
                  style={statusColor(row.lastRunStatusName)}
                />
              </Box>
            ) : null}
          </Box>
        </Tooltip>
      ),
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      align: "right",
      label: getTranslation("Actions", "Actions", "Aktionen"),
    },
  ];

  return (
    <div>
      <CardComponent title={getTranslation("Cron", "Cron", "Cron")}>
        <CardContent>
          <SchedulePanel schedules={schedules} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={getTranslation("Search", "Rechercher", "Suchen")}
                value={searchString}
                onChange={(e) => setSearchString(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") applyFilter();
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={getTranslation("App", "Application", "Anwendung")}
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
              >
                <MenuItem value="">
                  {getTranslation("All", "Tous", "Alle")}
                </MenuItem>
                {apps.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={getTranslation("Type", "Type", "Typ")}
                value={execution}
                onChange={(e) => setExecution(e.target.value)}
              >
                <MenuItem value="">
                  {getTranslation("All", "Tous", "Alle")}
                </MenuItem>
                {executions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                select
                fullWidth
                size="small"
                variant="outlined"
                label={getTranslation("Status", "Statut", "Status")}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">
                  {getTranslation("All", "Tous", "Alle")}
                </MenuItem>
                <MenuItem value="active">
                  {getTranslation("Active", "Actif", "Aktiv")}
                </MenuItem>
                <MenuItem value="disabled">
                  {getTranslation("Disabled", "Désactivé", "Deaktiviert")}
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <ButtonWithLoading
                label={getTranslation("Filter", "Filtrer", "Filtern")}
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => applyFilter()}
              />
              <ButtonWithLoading
                label={getTranslation("Add cron", "Ajouter", "Hinzufügen")}
                size="medium"
                variant="contained"
                color="secondary"
                onClick={() => setEditJob({})}
              />
            </Grid>
          </Grid>

          <TableComponent
            dense
            loading={isLoading}
            colums={columns}
            count={cronJobs?.totalCount}
            rows={cronJobs?.data}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={(event, newPage) => setPage(newPage)}
            handleChangeRowsPerPage={(e) => handleChangeRowsPerPage(e)}
            viewEdit
            onEdit={(row) => setLogsJob(row)}
            viewDelete
            onDelete={(row) => setDeleteJob(row)}
            actions={[
              {
                component: (rowData) => (
                  <ButtonWithLoading
                    label={getTranslation("Logs", "Journaux", "Protokolle")}
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => setLogsJob(rowData)}
                  />
                ),
              },
              {
                component: (rowData) => (
                  <ButtonWithLoading
                    label={getTranslation("Run now", "Exécuter", "Jetzt starten")}
                    size="small"
                    variant="contained"
                    color="primary"
                    loading={runningId === rowData.id}
                    onClick={() => runNow(rowData)}
                  />
                ),
              },
              {
                component: (rowData) => (
                  <ButtonWithLoading
                    label={
                      rowData.isActive
                        ? getTranslation("Disable", "Désactiver", "Deaktivieren")
                        : getTranslation("Enable", "Activer", "Aktivieren")
                    }
                    size="small"
                    variant="outlined"
                    onClick={() => toggleActive(rowData)}
                  />
                ),
              },
              {
                component: (rowData) => (
                  <ButtonWithLoading
                    label={getTranslation("Edit", "Modifier", "Bearbeiten")}
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => setEditJob(rowData)}
                  />
                ),
              },
            ]}
          />
        </CardContent>
      </CardComponent>

      <DialogComponent
        open={Boolean(editJob)}
        onClose={() => setEditJob(null)}
        fullWidth
        maxWidth="md"
        title={
          editJob && editJob.id
            ? getTranslation("Edit cron job", "Modifier la tâche cron", "Cron-Job bearbeiten")
            : getTranslation("Add cron job", "Ajouter une tâche cron", "Cron-Job hinzufügen")
        }
      >
        {editJob ? (
          <AddEditCronJob
            cronJob={editJob}
            apps={apps}
            executions={executions}
            onClose={() => setEditJob(null)}
            onSaved={() => {
              setEditJob(null);
              getCronJobs();
            }}
          />
        ) : null}
      </DialogComponent>

      <DialogComponent
        open={Boolean(deleteJob)}
        onClose={() => setDeleteJob(null)}
        fullWidth
        maxWidth="sm"
        title={getTranslation("Delete cron job", "Supprimer la tâche cron", "Cron-Job löschen")}
      >
        <Box p={2}>
          <Typography>
            {getTranslation(
              "This removes the job from every run. Its log entries are kept.",
              "La tâche est retirée de toutes les exécutions. Ses journaux sont conservés.",
              "Der Job wird aus allen Läufen entfernt. Seine Protokolle bleiben erhalten."
            )}
          </Typography>
          <Box mt={1}>
            <Typography variant="body2">
              <b>
                {deleteJob?.appName} / {deleteJob?.route}
              </b>
            </Typography>
          </Box>
        </Box>
        <DialogActions>
          <ButtonWithLoading
            label={getTranslation("Delete", "Supprimer", "Löschen")}
            variant="contained"
            color="secondary"
            onClick={() => confirmDelete()}
          />
          <ButtonWithLoading
            label={getTranslation("Cancel", "Annuler", "Absagen")}
            variant="outlined"
            onClick={() => setDeleteJob(null)}
          />
        </DialogActions>
      </DialogComponent>

      {logsJob ? (
        <CronJobDetailsDialog
          cronJob={logsJob}
          levels={levels}
          onClose={() => setLogsJob(null)}
        />
      ) : null}
    </div>
  );
};

export default Cron;
